import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/NotaryNodeMessage.css';
import Header from './Header';
import Footer from './Footer';

const NotaryNodeMessage: React.FC = () => {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App">
      <Header />
      <main>
        <section className="section notary-message-section">
          <div className="container">
            <div className="section-header">
              <h2>A Message to Komodo Notary Node Operators</h2>
            </div>
            
            <div className="notary-message-content">
              <div className="notary-message-container">
                <div className="notary-message-info">
                  <div className="message-body">
                <p>Dear Komodo Notary Node Operators,</p>
                
                <p>
                  As you are likely aware, Komodo's 8th and final Notary Node season is drawing to a close. The season will conclude at block <strong>4771595</strong>, which is anticipated around <strong>January 6, 2026</strong>. Following this block, the Komodo (KMD) network will transition to a pure Proof-of-Work (PoW) chain utilizing the Equihash 200.9 algorithm. This "dPoW sunset" marks a significant shift, as the responsibility for block creation will be entirely transferred to the broader community of PoW miners, with the network's security relying on public mining pools and individual miners.
                </p>
                
                <p>
                  The latest Komodo daemon update, version 0.9.2 ("Draco Typhon"), facilitates this transition by disabling the dPoW mechanism and introducing a new feature called "auto checkpointing."
                </p>
                
                <p>
                  We, the Komodo Classic (KMDCL) team, have made a deliberate decision to preserve the foundational principles of Komodo. Our fork, Komodo Classic, will <strong>not</strong> implement auto checkpointing. Instead, we will maintain the robust and proven dPoW consensus mechanism. This means that in the Komodo Classic ecosystem, Notary Nodes will continue to be an integral part of the network long after block 4771595. You will retain your ability to perform "easy-mining," generating blocks with minimum difficulty (<code>mindiff</code>) and earning a block reward of <strong>3 KMDCL</strong>, just as originally designed in the Komodo consensus.
                </p>
                
                <h2>A Seamless Opportunity for Season 8 Operators</h2>
                
                <p>
                  To ensure a smooth and rewarding transition for current operators, our team has intentionally kept the Season 8 Notary Node pubkeys unchanged in Komodo Classic. This means that <strong>any Notary Node active during Komodo's Season 8 can seamlessly continue its operations on the Komodo Classic network</strong> and keep earning rewards for its vital contributions.
                </p>
                
                <p>
                  In addition to the 3 KMDCL block reward, Komodo Classic is reactivating two highly-valued features: the <strong>5% Active User Reward (AUR)</strong> and <strong>private (shielded) transactions</strong>, further enhancing the utility and value of the network.
                </p>
                
                <h2>How to Get Started with Komodo Classic</h2>
                
                <p>
                  Continuing your work as a Notary Node on our network is straightforward. A few hours before the hard fork (you can track the exact timing on our dynamic countdown timer at <a href="https://countdown.kmdclassic.com/" target="_blank" rel="noopener noreferrer">https://countdown.kmdclassic.com/</a>), simply follow these steps on your server:
                </p>
                
                <ol>
                  <li><strong>Copy your existing <code>.komodo</code> directory</strong> (including your <code>wallet.dat</code> and other configurations) to a new directory named <code>.kmdclassic</code>.</li>
                  <li><strong>Rename your <code>komodo.conf</code> file</strong> to <code>kmdclassic.conf</code>.</li>
                  <li><strong>Build and run the <code>kmdclassicd</code> daemon</strong> from our official GitHub repository: <a href="https://github.com/kmdclassic/KmdClassic" target="_blank" rel="noopener noreferrer">https://github.com/kmdclassic/KmdClassic</a>.</li>
                </ol>
                
                <p>
                  The <code>kmdclassicd</code> daemon is designed as a <strong>drop-in replacement</strong> for <code>komodod</code>. Simply launch it with the same parameters you use for your current NN, enable mining, and you will begin receiving KMDCL rewards.
                </p>
                
                <h2>Enhanced RPC for Simplified Operations</h2>
                
                <p>
                  We've also streamlined the technical experience. You no longer need the <code>iguana</code> core for <code>splitfunds</code>. To generate the UTXOs required for mining, you can now use a new RPC command: <code>nn_split</code>.
                </p>
                
                <blockquote>
                  This powerful RPC can create <code>notaryvins</code> (the special P2PK UTXOs used by Notary Nodes for mining and notarizing) from any UTXOs in your wallet. It offers flexible operation modes; for instance, it can merge all your UTXOs in a single transaction to create a fresh set of <code>notaryvins</code>, or it can intelligently select low-amount UTXOs. This command serves as an excellent and efficient replacement for the old <code>autosplit</code> function in Iguana or any custom scripts you might be using.
                </blockquote>
                
                <p>
                  For enhanced monitoring, you can also use the <code>nn_getwalletinfo</code> RPC to access detailed statistics about your Notary Node's performance.
                </p>
                
                <h2>Join Us in Building the Future</h2>
                
                <p>
                  With the dPoW sunset on the main chain, you don't have to let your server infrastructure - which may be prepaid for months to come - sit idle. You have a valuable opportunity to continue your work, support a dedicated network, and earn rewards as a Komodo Classic Notary Node.
                </p>
                
                <p>
                  Furthermore, our team and community are in active discussions with the <strong>PIRATE (ARRR)</strong> team to have Komodo Classic Notary Nodes notarize the PIRATE chain to KMDCL, and KMDCL to LTC. Detailed instructions and further announcements on this exciting development will be shared on our official channels soon.
                </p>
                
                <p>
                  We are building Komodo Classic to honor the original vision of founder of Komodo, jl777c, and we believe in a future of mutual growth and collaboration. We are excited about the prospect of working with experienced operators like you and hope to see you among our ranks.
                </p>
                
                <p>
                  Should you have any questions, please do not hesitate to reach out to us through any of the channels listed on our official website: <a href="https://kmdclassic.com/" target="_blank" rel="noopener noreferrer">https://kmdclassic.com/</a>.
                </p>
                
                <p>
                  We look forward to a successful partnership.
                </p>
                
                <p className="signature">
                  Sincerely,<br />
                  The Komodo Classic Team
                </p>
                  </div>
                </div>
              </div>
              
              <div className="back-link">
                <Link to="/">← Back to Home</Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default NotaryNodeMessage;

